import dayjs from "dayjs";

import { DATE_FORMATS } from "@tether-web-portal/constants/dateFormats";
import { DeviceType, OccupancyLineDeviceTypes } from "@tether-web-portal/types/DeviceType";
import { MonitoringHours } from "@tether-web-portal/types/MonitoringHours";
import { Property } from "@tether-web-portal/types/Property";
import { PropertyDetail } from "@tether-web-portal/types/PropertyDetail";
import { RoomLocationAggregate } from "@tether-web-portal/types/RoomLocation";
import { RoomType } from "@tether-web-portal/components/create-room/room-icon/RoomIcon";

import {
  IOccupancyBreakdownDataPoint,
  IOccupancyBreakdownFilters,
  IOccupancyBreakdownSummary,
  IOccupancyBreakdownWorkerOutput,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyBreakdown";
import {
  OccupancyExportData,
  OccupancyExportRoom,
  OccupancyExportRow,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyExport";
import {
  OccupancyAggregate,
  OccupancyTilesData,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyTiles";
import {
  IOccupancyFloor,
  IOccupancyPageFilters,
  IOccupancySpace,
  IOccupancyWorkerBaseProps,
  IRoomOccupancyAggregates,
  OCCUPANCY_DEVICE_TYPES,
  RoomAggregatedData,
  RoomLocationAggregatedData,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyWorker";
import {
  IOccupancyUtilisationRow,
  IOccupancyUtilisationSpaceChildRow,
  IOccupancyUtilisationTableFilters,
  IOccupancyUtilisationWorkerOutput,
  OccupancyUtilisationRowType,
  OccupancyUtilisationSpaceChildType,
} from "@tether-web-portal/web-workers/occupancy/types/OccupancyUtilisation";
import { SeriesType } from "@tether-web-portal/components/property-graph/GraphUtils/SeriesType";
import { SeriesTypeReading } from "@tether-web-portal/components/property-graph/GraphUtils/SeriesTypeReading";

import {
  applyPageLevelFilters,
  checkIfRoomLocationHasActiveOccupancyChildrenDevice,
  getRoomsWithAggregates,
  isRoomCapacityAble,
  makeFloorLevelSpaceName,
} from "./helpers";
import {
  OccupancyMetricChoice,
  detectRoomOccupancyMetric,
  getBucketOccupiedFraction,
  getBucketTotalIn,
  getBucketTotalOut,
  getRoomBucketOccupancyCount,
  getRoomLocationBucketOccupancyCount,
  looksLikeOccupancyResetBucket,
} from "./metrics";
import {
  calculateTotalMonitoredHours,
  filterToMonitoredBuckets,
} from "./monitoringHours";
import { asFraction01, Fraction01 } from "./units";
import { getUtilisationLevel } from "./utilisationLevel";
import { buildOccupancyDiagnostics, OccupancyDiagnostics } from "./debug";
import { formatLocalParts, localiseTimestamp } from "./fastTz";

/**
 * The single authoritative model for the Occupancy page. Built once per
 * context render; every UI surface is a projection of this object. No
 * downstream consumer should re-aggregate, re-filter, or re-bucket.
 */
export interface OccupancyModel {
  tiles: OccupancyTilesData;
  utilisation: IOccupancyUtilisationWorkerOutput;
  breakdown: IOccupancyBreakdownWorkerOutput;
  lowResBreakdown: IOccupancyBreakdownDataPoint[];
  export: OccupancyExportData;
  /** Inspection data for the browser console. See lib/occupancy/debug.ts. */
  diagnostics: OccupancyDiagnostics;
}

/**
 * Only the fields of IOccupancyBreakdownFilters that actually affect what the
 * model produces. `metricToDisplay` is deliberately excluded — it's a display
 * concern read directly by the bar chart / heatmap, and including it here
 * would cause the whole model to rebuild whenever the user toggles between
 * "Occupancy" and "Capacity %" in the dropdown.
 */
export interface ModelBreakdownFilters {
  floorIndexToDisplay: number;
  spaceToDisplay: string;
}

/**
 * Granularity the bar chart should roll up the high-res breakdown to.
 * Chosen by the context based on the selected date range.
 */
export type BarChartResolution = "hour" | "day";

export interface BuildOccupancyModelInput extends IOccupancyWorkerBaseProps {
  roomLocationAggregations?: RoomLocationAggregatedData[];
  isLiveView: boolean;
  breakdownFilters: ModelBreakdownFilters;
  tableFilters?: IOccupancyUtilisationTableFilters;
  /**
   * Resolution to roll the bar-chart breakdown up to. When omitted, the
   * bar-chart data mirrors the high-res heatmap data.
   */
  barChartResolution?: BarChartResolution;
  /**
   * The time range the API calls spanned. Used purely for the monitored-hours
   * total (which powers `usageTimeHours` on the table).
   */
  timeRange: { fromTimestamp: string; toTimestamp: string };
}

// ---------- Utility ----------

const ceilTo3 = (f: number): Fraction01 => asFraction01(Math.ceil(f * 1000) / 1000);

const fractionOrNull = (numerator: number, denominator: number): Fraction01 | null =>
  denominator > 0 ? ceilTo3(numerator / denominator) : null;

interface RoomBucket {
  startTimestamp: string;
  endTimestamp: string;
  /** Room-level occupancy count per rule 4 (regionOccupancy if present). */
  occupancyMax: number;
  occupancyMean: number;
  occupancyMin: number;
  occupancyLast: number | undefined;
  /**
   * Fraction (0..1) of THIS bucket's duration that the room was occupied —
   * sourced from the API's `occupied.mean` metric, which is the time-weighted
   * average of the 0/1 `occupied` flag over the bucket. Falls back to
   * `occupancyMax > 0 ? 1 : 0` when the API didn't emit `occupied` for that
   * bucket. Used as the per-cell value when the heatmap / bar chart metric
   * is set to Usage %.
   */
  occupiedFraction: number;
  /** True when this bucket was kept by the monitoring-hours filter. */
  monitored: boolean;
  /** True when this is the synthetic "occupancy reset" sample; excluded from stats. */
  isResetSample: boolean;
  totalIn: number;
  totalOut: number;
}

interface RoomModel {
  id: string;
  name: string;
  type: RoomType;
  floorIndex: number;
  capacity: number | undefined;
  isCapacityAble: boolean;
  deviceTypesByCount: Record<string, number>;
  /**
   * Which occupancy metric this room has committed to for the whole window
   * (see `detectRoomOccupancyMetric`). All `buckets` values were read through
   * this choice; buckets that didn't carry the chosen metric are present
   * with `occupancyMax === 0` so they don't contribute to totals.
   */
  occupancyMetric: OccupancyMetricChoice;
  /** Per-bucket room data. Includes both monitored and unmonitored buckets. */
  buckets: RoomBucket[];
  /** Active child room-locations (regions/lines) with their own bucket data. */
  children: RoomChildModel[];
}

interface RoomChildModel {
  id: string;
  name: string;
  childType: OccupancyUtilisationSpaceChildType;
  buckets: RoomBucket[];
  dwellTimeBuckets: { monitored: boolean; value: number }[];
}

// ---------- Per-room preparation ----------

const prepareRoomBuckets = (
  room: IRoomOccupancyAggregates,
  monitoringHours: MonitoringHours[],
  propertyTimezone: string,
): { buckets: RoomBucket[]; occupancyMetric: OccupancyMetricChoice } => {
  // Deduplicate incoming aggregates by (startTimestamp, endTimestamp). The
  // aggregates endpoint has been observed to return the same bucket twice
  // for a room in some edge cases (investigation pending); left unchecked,
  // duplicates cause the building-wide per-bucket total to be double-counted
  // while per-room stats only see the single bucket, breaking the invariant
  // `sum_of_per_room_peaks >= max_building_bucket`. Keep the first occurrence
  // of each key.
  const seen = new Set<string>();
  const uniqueAggregates = room.aggregations.filter((agg) => {
    const key = `${agg.startTimestamp}_${agg.endTimestamp}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Decide the room's occupancy metric ONCE from the full bucket set. Rule 4:
  // if any bucket has `regionOccupancy`, the room is region-based and we must
  // ignore any stragglers that only carry `occupancy` (they'd mix zones-busy
  // with people-present, inflating the total).
  const occupancyMetric = detectRoomOccupancyMetric(uniqueAggregates);

  const monitoredSet = new Set(
    filterToMonitoredBuckets(uniqueAggregates, monitoringHours, propertyTimezone).map(
      (b) => b.startTimestamp,
    ),
  );
  const buckets = uniqueAggregates.map((agg) => {
    const isReset = looksLikeOccupancyResetBucket(agg);
    const occupancyMax = getRoomBucketOccupancyCount(agg, "max", occupancyMetric) ?? 0;
    const occupiedMean = getBucketOccupiedFraction(agg);
    return {
      startTimestamp: agg.startTimestamp,
      endTimestamp: agg.endTimestamp,
      occupancyMax,
      occupancyMean: getRoomBucketOccupancyCount(agg, "mean", occupancyMetric) ?? 0,
      occupancyMin: getRoomBucketOccupancyCount(agg, "min", occupancyMetric) ?? 0,
      occupancyLast: getRoomBucketOccupancyCount(agg, "last", occupancyMetric),
      // `occupied.mean` is only emitted when the API has the derived `occupied`
      // metric for the room. When absent (e.g. region-only rooms), fall back
      // to the binary "at least one occupant" flag so the heatmap cell still
      // colours correctly instead of going black.
      occupiedFraction: occupiedMean > 0 ? occupiedMean : occupancyMax > 0 ? 1 : 0,
      monitored: monitoredSet.has(agg.startTimestamp) && !isReset,
      isResetSample: isReset,
      totalIn: getBucketTotalIn(agg),
      totalOut: getBucketTotalOut(agg),
    };
  });

  return { buckets, occupancyMetric };
};

const prepareChildBuckets = (
  agg: RoomLocationAggregate,
  monitoredKey: Set<string>,
  parentMetricChoice: OccupancyMetricChoice,
): RoomBucket => {
  const isReset = looksLikeOccupancyResetBucket(agg);
  const occupancyMax = getRoomLocationBucketOccupancyCount(agg, "max", parentMetricChoice) ?? 0;
  const occupiedMean = getBucketOccupiedFraction(agg);
  return {
    startTimestamp: agg.startTimestamp,
    endTimestamp: agg.endTimestamp,
    occupancyMax,
    occupancyMean: getRoomLocationBucketOccupancyCount(agg, "mean", parentMetricChoice) ?? 0,
    occupancyMin: getRoomLocationBucketOccupancyCount(agg, "min", parentMetricChoice) ?? 0,
    occupancyLast: getRoomLocationBucketOccupancyCount(agg, "last", parentMetricChoice),
    occupiedFraction: occupiedMean > 0 ? occupiedMean : occupancyMax > 0 ? 1 : 0,
    monitored: monitoredKey.has(agg.startTimestamp) && !isReset,
    isResetSample: isReset,
    totalIn: getBucketTotalIn(agg),
    totalOut: getBucketTotalOut(agg),
  };
};

const prepareRoomModel = (
  room: IRoomOccupancyAggregates,
  roomLocationAggregations: RoomLocationAggregatedData[] | undefined,
  monitoringHours: MonitoringHours[],
  propertyTimezone: string,
): RoomModel => {
  const { buckets, occupancyMetric } = prepareRoomBuckets(room, monitoringHours, propertyTimezone);

  // Device counts for the "Devices" column.
  const deviceTypesByCount: Record<string, number> = {};
  for (const rl of room.roomLocations) {
    const t = rl.device?.type;
    if (t && OCCUPANCY_DEVICE_TYPES.includes(t as DeviceType)) {
      deviceTypesByCount[t] = (deviceTypesByCount[t] ?? 0) + 1;
    }
  }

  // Children (REGION / LINE).
  const children: RoomChildModel[] = [];
  if (roomLocationAggregations?.length) {
    const active = room.roomLocations.filter(checkIfRoomLocationHasActiveOccupancyChildrenDevice);
    for (const rl of active) {
      const agg = roomLocationAggregations.find((x) => x?.roomLocationId === rl.id);
      if (!agg) continue;

      const monitoredKey = new Set(
        filterToMonitoredBuckets(agg.aggregatedData, monitoringHours, propertyTimezone).map(
          (b) => b.startTimestamp,
        ),
      );
      const childBuckets = agg.aggregatedData.map((a) =>
        prepareChildBuckets(a, monitoredKey, occupancyMetric),
      );
      const dwellTimeBuckets = agg.aggregatedData.map((a) => ({
        monitored: monitoredKey.has(a.startTimestamp),
        value: a.metrics[SeriesTypeReading[SeriesType.DwellTimeMax]]?.max ?? 0,
      }));

      children.push({
        id: rl.id,
        name: rl.name,
        childType: OccupancyLineDeviceTypes.has(rl.device?.type as DeviceType)
          ? OccupancyUtilisationSpaceChildType.LINE
          : OccupancyUtilisationSpaceChildType.REGION,
        buckets: childBuckets,
        dwellTimeBuckets,
      });
    }
  }

  return {
    id: room.id,
    name: room.name,
    type: room.type,
    floorIndex: room.floorIndex,
    capacity: room.capacity,
    isCapacityAble: isRoomCapacityAble(room),
    deviceTypesByCount,
    occupancyMetric,
    buckets,
    children,
  };
};

// ---------- Per-room row calculation ----------

const extractActiveBuckets = (buckets: RoomBucket[]): RoomBucket[] =>
  buckets.filter((b) => !b.isResetSample);

const latestBucketOccupancy = (buckets: RoomBucket[]): number => {
  // Buckets are ordered chronologically. Find the MOST RECENT bucket with a
  // `.last` reading; fall back to that bucket's `.max`.
  const active = extractActiveBuckets(buckets);
  for (let i = active.length - 1; i >= 0; i--) {
    const b = active[i];
    if (b.occupancyLast !== undefined) return b.occupancyLast;
    if (b.occupancyMax > 0) return b.occupancyMax;
  }
  return 0;
};

const sumInOut = (buckets: RoomBucket[]): { entered: number; exited: number } => {
  // Counts (entered/exited/footTraffic) span every monitoring window per
  // Rule 1: people walking in at 3am really walked in, and only time-weighted
  // usage % is scoped to monitored hours. We DO drop `isResetSample` buckets
  // — those are sensor-reset noise, the same exclusion `buildBuildingBuckets`
  // and `extractActiveBuckets` apply, so room/aggregate/child rows and tiles
  // all read the same population of buckets.
  let entered = 0;
  let exited = 0;
  for (const b of buckets) {
    if (b.isResetSample) continue;
    entered += b.totalIn;
    exited += b.totalOut;
  }
  return { entered, exited };
};

const buildRoomRow = (
  room: RoomModel,
  isLiveView: boolean,
  totalMonitoredHours: number | undefined,
): IOccupancyUtilisationRow => {
  const activeBuckets = extractActiveBuckets(room.buckets);
  const monitoredBuckets = activeBuckets.filter((b) => b.monitored);

  // Peak uses all reported buckets (matches the heatmap / bar chart, which
  // show every bucket). Min / mean / usage are monitored-only — those are
  // business-hours questions; counting 2am zeros into the average dilutes
  // the signal.
  const allOccupancyMax = activeBuckets.map((b) => b.occupancyMax);
  const monitoredOccupancyMean = monitoredBuckets.map((b) => b.occupancyMean);
  const monitoredOccupancyMin = monitoredBuckets.map((b) => b.occupancyMin);

  const roomPeakCount = allOccupancyMax.length ? Math.max(...allOccupancyMax) : 0;
  const roomMinCount = monitoredOccupancyMin.length ? Math.min(...monitoredOccupancyMin) : 0;
  const roomMeanCount = monitoredOccupancyMean.length
    ? Math.ceil(monitoredOccupancyMean.reduce((a, b) => a + b, 0) / monitoredOccupancyMean.length)
    : 0;

  const liveCount = latestBucketOccupancy(room.buckets);

  // Usage % = time-weighted mean of per-bucket `occupied.mean` across
  // monitored hours. Matches the heatmap / bar chart semantic: "on average,
  // what fraction of each monitored hour was the space occupied?".
  const usageFraction: Fraction01 = monitoredBuckets.length
    ? ceilTo3(
        monitoredBuckets.reduce((s, b) => s + b.occupiedFraction, 0) / monitoredBuckets.length,
      )
    : asFraction01(0);

  const capacityFraction = room.isCapacityAble
    ? fractionOrNull(isLiveView ? liveCount : roomPeakCount, room.capacity ?? 0)
    : null;

  const { entered, exited } = sumInOut(room.buckets);

  const childRows: IOccupancyUtilisationSpaceChildRow[] = room.children.map((child) =>
    buildChildRow(child, isLiveView, totalMonitoredHours),
  );

  return {
    rowType: OccupancyUtilisationRowType.ROOM,
    spaceName: room.name,
    spaceType: room.type,
    spaceId: room.id,
    spaceFloorIndex: room.floorIndex,
    spaceCapacity: room.capacity,
    isCapacityAble: room.isCapacityAble,
    usage: {
      percentage: usageFraction,
      utilisationLevel: getUtilisationLevel(usageFraction),
      isOccupied: isLiveView ? liveCount > 0 : usageFraction > 0 || roomPeakCount > 0,
      usageTimeHours:
        !isLiveView && totalMonitoredHours !== undefined
          ? usageFraction * totalMonitoredHours
          : undefined,
    },
    occupants: {
      min: roomMinCount,
      max: roomPeakCount,
      mean: roomMeanCount,
    },
    capacityUsage: {
      percentage: capacityFraction,
      latestCount: room.isCapacityAble ? (isLiveView ? liveCount : roomPeakCount) : null,
      entered: room.isCapacityAble ? entered : null,
      exited: room.isCapacityAble ? exited : null,
      footTraffic: room.isCapacityAble ? entered + exited : null,
      utilisationLevel: getUtilisationLevel(capacityFraction),
    },
    devices: Object.entries(room.deviceTypesByCount).map(([type, count]) => ({
      type: type as DeviceType,
      count,
    })),
    spaceChildren: childRows,
  };
};

const buildChildRow = (
  child: RoomChildModel,
  isLiveView: boolean,
  totalMonitoredHours: number | undefined,
): IOccupancyUtilisationSpaceChildRow => {
  const activeBuckets = extractActiveBuckets(child.buckets);
  const monitoredBuckets = activeBuckets.filter((b) => b.monitored);

  // Peak over all buckets (matches heatmap); min / mean monitored-only.
  const maxOcc = activeBuckets.length ? Math.max(...activeBuckets.map((b) => b.occupancyMax)) : 0;
  const minOcc = monitoredBuckets.length ? Math.min(...monitoredBuckets.map((b) => b.occupancyMin)) : 0;
  const meanOcc = monitoredBuckets.length
    ? Math.ceil(monitoredBuckets.reduce((s, b) => s + b.occupancyMean, 0) / monitoredBuckets.length)
    : 0;

  const monitoredDwell = child.dwellTimeBuckets.filter((d) => d.monitored).map((d) => d.value);
  const maxDwell = monitoredDwell.length ? Math.max(...monitoredDwell) : 0;
  const minDwell = monitoredDwell.length ? Math.min(...monitoredDwell) : 0;
  const meanDwell = monitoredDwell.length
    ? Math.ceil(monitoredDwell.reduce((a, b) => a + b, 0) / monitoredDwell.length)
    : 0;

  const totalIn = activeBuckets.reduce((s, b) => s + b.totalIn, 0);
  const totalOut = activeBuckets.reduce((s, b) => s + b.totalOut, 0);

  // Same semantic as the room row: mean of `occupied.mean` across monitored
  // buckets, not a count of buckets-with-any-occupancy.
  const usageFraction: Fraction01 = monitoredBuckets.length
    ? ceilTo3(
        monitoredBuckets.reduce((s, b) => s + b.occupiedFraction, 0) / monitoredBuckets.length,
      )
    : asFraction01(0);

  const liveOcc = latestBucketOccupancy(child.buckets);

  return {
    name: child.name,
    childType: child.childType,
    dwellTime: { min: minDwell, mean: meanDwell, max: maxDwell },
    occupants: {
      min: minOcc,
      mean: meanOcc,
      max: maxOcc,
      entered: totalIn,
      exited: totalOut,
    },
    usage: {
      isOccupied: isLiveView ? liveOcc > 0 : usageFraction > 0 || maxOcc > 0,
      percentage: usageFraction,
      utilisationLevel: getUtilisationLevel(usageFraction),
      usageTimeHours:
        !isLiveView && totalMonitoredHours !== undefined
          ? usageFraction * totalMonitoredHours
          : undefined,
    },
  };
};

// ---------- Building / floor aggregation ----------

interface BuildingBucket {
  startTimestamp: string;
  endTimestamp: string;
  totalOccupancy: number;
  /**
   * Mean of per-room `occupiedFraction` at this bucket. Answers "on average
   * across contributing rooms, what fraction of this bucket's duration was
   * each room occupied?". Drives the Usage % metric on the heatmap / bar chart
   * — without this, per-bucket usage would be binary (every non-empty cell
   * rendering as 100%).
   */
  meanOccupiedFraction: number;
  monitored: boolean;
  totalIn: number;
  totalOut: number;
}

/**
 * Align every room's buckets by (startTimestamp, endTimestamp) and sum the
 * per-room occupancy at each time point to get "simultaneous" occupancy.
 * This is what powers the max-simultaneous calculation (rule 1) and the
 * time-based usage % (rule 2) at building/floor level.
 */
const buildBuildingBuckets = (rooms: RoomModel[]): BuildingBucket[] => {
  interface Accum extends BuildingBucket {
    occupiedFractionSum: number;
    occupiedFractionCount: number;
  }
  const byKey = new Map<string, Accum>();
  for (const room of rooms) {
    for (const b of room.buckets) {
      if (b.isResetSample) continue;
      const key = `${b.startTimestamp}_${b.endTimestamp}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          startTimestamp: b.startTimestamp,
          endTimestamp: b.endTimestamp,
          totalOccupancy: b.occupancyMax,
          meanOccupiedFraction: b.occupiedFraction,
          occupiedFractionSum: b.occupiedFraction,
          occupiedFractionCount: 1,
          monitored: b.monitored,
          totalIn: b.totalIn,
          totalOut: b.totalOut,
        });
      } else {
        existing.totalOccupancy += b.occupancyMax;
        existing.occupiedFractionSum += b.occupiedFraction;
        existing.occupiedFractionCount += 1;
        existing.meanOccupiedFraction =
          existing.occupiedFractionSum / existing.occupiedFractionCount;
        existing.monitored = existing.monitored || b.monitored;
        existing.totalIn += b.totalIn;
        existing.totalOut += b.totalOut;
      }
    }
  }
  return Array.from(byKey.values())
    .sort((a, b) => a.startTimestamp.localeCompare(b.startTimestamp))
    .map(({ occupiedFractionSum: _sum, occupiedFractionCount: _count, ...bucket }) => bucket);
};

const roomMonitoredBuckets = (r: RoomModel): RoomBucket[] =>
  r.buckets.filter((b) => b.monitored && !b.isResetSample);

/**
 * A room's "Usage %" over the monitored period = time-weighted mean of the
 * per-bucket `occupied.mean` values during monitored hours. Conceptually:
 * "over all monitored hours, on average what fraction of each hour was the
 * room actually occupied?". This matches the per-bucket values rendered in
 * the heatmap / bar chart, so the Total row and the summary pill are
 * averages of what the user can see on the chart rather than a separate
 * count-of-bucket metric that diverged.
 */
const roomUsageFraction = (r: RoomModel): number => {
  const monitored = roomMonitoredBuckets(r);
  if (!monitored.length) return 0;
  const total = monitored.reduce((s, b) => s + b.occupiedFraction, 0);
  return total / monitored.length;
};

const buildSummaryForRooms = (
  rooms: RoomModel[],
  capacityLimit: number,
  isLiveView: boolean,
): IOccupancyBreakdownSummary => {
  const buildingBuckets = buildBuildingBuckets(rooms);
  const monitored = buildingBuckets.filter((b) => b.monitored);

  // Max-simultaneous spans EVERY bucket: if 50 people were in the building
  // at 3am, that really happened and the Total Occupancy tile has to
  // reflect it. Only time-weighted usage (`usagePercentage` further down
  // and `roomUsageFraction`) is scoped to monitored hours.
  //
  // Min / mean stay monitored-only so they don't get dragged to 0 by every
  // idle overnight bucket — the business-hours min/mean answer "when the
  // building is open, how lightly / typically loaded is it?" which is the
  // more useful question for these two summary pills. This mirrors the
  // per-room row semantic (`buildRoomRow`: peak=all, min/mean=monitored).
  const maxSimultaneous = buildingBuckets.length
    ? Math.max(...buildingBuckets.map((b) => b.totalOccupancy))
    : 0;
  const minSimultaneous = monitored.length ? Math.min(...monitored.map((b) => b.totalOccupancy)) : 0;
  const meanSimultaneous = monitored.length
    ? Math.ceil(monitored.reduce((s, b) => s + b.totalOccupancy, 0) / monitored.length)
    : 0;

  // Aggregate usage % = the simple mean of constituent rooms' usage fractions.
  // The more literal "fraction of monitored time where any room had someone"
  // reads as 100% as soon as one room is always busy, which surprises users
  // who read it alongside individual room percentages below 100%.
  const perRoomUsages = rooms.map(roomUsageFraction);
  const usageFraction: Fraction01 = perRoomUsages.length
    ? ceilTo3(perRoomUsages.reduce((a, b) => a + b, 0) / perRoomUsages.length)
    : asFraction01(0);

  const capacityPercentageMax = fractionOrNull(maxSimultaneous, capacityLimit);

  const totalIn = buildingBuckets.reduce((s, b) => s + b.totalIn, 0);
  const totalOut = buildingBuckets.reduce((s, b) => s + b.totalOut, 0);

  const latestCount = rooms.reduce((s, r) => s + latestBucketOccupancy(r.buckets), 0);
  const headlineOccupancy = isLiveView ? latestCount : maxSimultaneous;

  return {
    occupancyLastCount: latestCount,
    occupancyMaxCount: headlineOccupancy,
    occupancyMinCount: minSimultaneous,
    occupancyAvgCount: meanSimultaneous,
    occupancyLimit: capacityLimit > 0 ? capacityLimit : null,
    occupancyTotalIn: totalIn,
    occupancyTotalOut: totalOut,
    occupancyFootTraffic: totalIn + totalOut,
    capacityPercentage: capacityPercentageMax,
    capacityUtilisationLevel: getUtilisationLevel(capacityPercentageMax),
    usagePercentage: usageFraction,
  };
};

const buildAggregateRow = (
  rooms: RoomModel[],
  rowType: OccupancyUtilisationRowType,
  spaceName: string,
  floorIndex: number | undefined,
  isLiveView: boolean,
  totalMonitoredHours: number | undefined,
): IOccupancyUtilisationRow => {
  const totalCapacity = rooms.reduce((s, r) => s + (r.capacity ?? 0), 0);
  const summary = buildSummaryForRooms(rooms, totalCapacity, isLiveView);

  // Counts span ALL buckets (Rule 1) — `summary` already sums entered/exited
  // across the full day. A monitored-only filter here would silently drop
  // overnight/off-hours traffic and diverge from the building totals.
  const entered = summary.occupancyTotalIn;
  const exited = summary.occupancyTotalOut;

  const isCapacityAble = rooms.some((r) => r.isCapacityAble);
  const deviceTypesByCount: Record<string, number> = {};
  for (const r of rooms) {
    for (const [type, count] of Object.entries(r.deviceTypesByCount)) {
      deviceTypesByCount[type] = (deviceTypesByCount[type] ?? 0) + count;
    }
  }

  // Capacity % must match the count we're displaying (`occupancyMaxCount`)
  // — which is already isLiveView-gated by `buildSummaryForRooms` (live =
  // sum of latest per room; historical = max-simultaneous). `buildRoomRow`
  // does the same split per-room. Reading `summary.capacityPercentage`
  // here would lock the building / floor rows to the historical peak %
  // even in live view, while the per-room rows next to them update to
  // live %, producing the inconsistency the breakdown summary pill is
  // free to keep (its scope is the displayed period, not "right now").
  const capacityFraction = isCapacityAble
    ? fractionOrNull(summary.occupancyMaxCount, totalCapacity)
    : null;

  return {
    rowType,
    spaceName,
    spaceFloorIndex: floorIndex,
    devices: Object.entries(deviceTypesByCount).map(([type, count]) => ({
      type: type as DeviceType,
      count,
    })),
    isCapacityAble,
    usage: {
      percentage: summary.usagePercentage as Fraction01,
      utilisationLevel: getUtilisationLevel(summary.usagePercentage as Fraction01),
      isOccupied: summary.usagePercentage > 0 || summary.occupancyMaxCount > 0,
      usageTimeHours:
        !isLiveView && totalMonitoredHours !== undefined
          ? summary.usagePercentage * totalMonitoredHours
          : undefined,
    },
    spaceCapacity: totalCapacity,
    occupants: {
      min: summary.occupancyMinCount,
      max: summary.occupancyMaxCount,
      mean: summary.occupancyAvgCount,
    },
    capacityUsage: {
      latestCount: isCapacityAble ? summary.occupancyMaxCount : null,
      percentage: capacityFraction,
      entered: isCapacityAble ? entered : null,
      exited: isCapacityAble ? exited : null,
      footTraffic: isCapacityAble ? entered + exited : null,
      utilisationLevel: getUtilisationLevel(capacityFraction),
    },
    spaceChildren: [],
  };
};

// ---------- Breakdown time-series ----------

const buildBreakdownData = (
  rooms: RoomModel[],
  capacityLimit: number,
): IOccupancyBreakdownDataPoint[] => {
  const buildingBuckets = buildBuildingBuckets(rooms);
  return buildingBuckets.map((b) => {
    const capacityFraction = fractionOrNull(b.totalOccupancy, capacityLimit);
    // Per-bucket Usage % is the mean of per-room `occupied.mean` across the
    // rooms contributing to this bucket — i.e. "how much of this bucket's
    // duration was each room, on average, occupied?". Using a binary
    // occupancy > 0 flag here makes every non-empty heatmap cell render as
    // 100% which obliterates the metric's resolution.
    const usagePerBucket: Fraction01 = ceilTo3(b.meanOccupiedFraction);
    return {
      startTimestamp: b.startTimestamp,
      endTimestamp: b.endTimestamp,
      breakdown: {
        usagePercentage: usagePerBucket,
        occupancyCount: b.totalOccupancy,
        occupancyLimit: capacityLimit > 0 ? capacityLimit : null,
        occupancyLastCount: b.totalOccupancy,
        capacityPercentage: capacityFraction,
        capacityUtilisationLevel:
          capacityFraction === null ? null : getUtilisationLevel(capacityFraction),
        entered: b.totalIn,
        exited: b.totalOut,
        footTraffic: b.totalIn + b.totalOut,
      },
    };
  });
};

// ---------- Tile aggregations (peak days / hours / glance) ----------

const aggregateBy = (
  rooms: RoomModel[],
  capacityLimit: number,
  propertyTimezone: string,
  format: string,
  isLiveView: boolean,
): OccupancyAggregate[] => {
  // Peak-days / peak-hours / glance show when the building is BUSY — they
  // group by business-hours bucket (day-of-week, hour-of-day, calendar day)
  // to highlight patterns in the operating window. Feeding in unmonitored
  // buckets adds flat 0-filled entries for nights/weekends which aren't
  // the question these tiles answer. The Total Occupancy tile (max-over-
  // all-hours, rule 1) is where an overnight spike shows up; these tiles
  // stay scoped to monitored hours for legibility.
  const buildingBuckets = buildBuildingBuckets(rooms).filter((b) => b.monitored);
  const groups = new Map<string, { id: string; order: number; peak: number }>();
  for (const b of buildingBuckets) {
    const parts = localiseTimestamp(b.startTimestamp, propertyTimezone);
    const id = formatLocalParts(parts, format);
    const order = Date.parse(b.startTimestamp);
    const existing = groups.get(id);
    if (!existing) {
      groups.set(id, { id, order, peak: b.totalOccupancy });
    } else {
      existing.peak = Math.max(existing.peak, b.totalOccupancy);
      existing.order = Math.min(existing.order, order);
    }
  }
  return Array.from(groups.values())
    .sort((a, b) => a.order - b.order)
    .map((g) => ({
      id: g.id,
      occupants: g.peak,
      // capacityLimit can be 0 when every contributing room is unconfigured;
      // returning 0 (rather than dividing by 1) keeps the bar empty instead of
      // rendering nonsense like 1500% over the unconfigured-rooms warning.
      usage:
        isLiveView || capacityLimit <= 0
          ? 0
          : Math.round(((g.peak / capacityLimit) * 100) * 10) / 10,
    }));
};

// ---------- Bar-chart rollup ----------

/**
 * Aggregate the high-res breakdown into coarser buckets for the bar chart.
 * For each target-resolution period (hour or day in the property timezone),
 * takes the MAX `occupancyCount` across the source buckets that fall into it,
 * sums `entered` / `exited`, and recomputes `capacityPercentage` against
 * the given capacity limit.
 *
 * Using max-over-sources means the bar chart reflects the peak observed
 * during that period — the same number the heatmap's hottest cell in that
 * period shows.
 */
const rollUpBreakdownBuckets = (
  buckets: IOccupancyBreakdownDataPoint[],
  resolution: BarChartResolution,
  capacityLimit: number,
  propertyTimezone: string,
): IOccupancyBreakdownDataPoint[] => {
  if (!buckets.length) return [];

  interface Group {
    key: string;
    order: number;
    firstStart: string;
    lastEnd: string;
    maxCount: number;
    maxUsage: number;
    totalIn: number;
    totalOut: number;
    latestCount: number;
  }
  const groups = new Map<string, Group>();

  for (const b of buckets) {
    const parts = localiseTimestamp(b.startTimestamp, propertyTimezone);
    const key =
      resolution === "hour"
        ? `${parts.dateKey}T${parts.hour < 10 ? "0" : ""}${parts.hour}`
        : parts.dateKey;
    const order = Date.parse(b.startTimestamp);
    const count = b.breakdown.occupancyCount ?? 0;
    const usage = b.breakdown.usagePercentage ?? 0;
    const g = groups.get(key);
    if (!g) {
      groups.set(key, {
        key,
        order,
        firstStart: b.startTimestamp,
        lastEnd: b.endTimestamp,
        maxCount: count,
        maxUsage: usage,
        totalIn: b.breakdown.entered,
        totalOut: b.breakdown.exited,
        latestCount: b.breakdown.occupancyLastCount,
      });
    } else {
      g.maxCount = Math.max(g.maxCount, count);
      g.maxUsage = Math.max(g.maxUsage, usage);
      g.totalIn += b.breakdown.entered;
      g.totalOut += b.breakdown.exited;
      g.latestCount = b.breakdown.occupancyLastCount;
      if (order < g.order) {
        g.order = order;
        g.firstStart = b.startTimestamp;
      }
      if (b.endTimestamp > g.lastEnd) g.lastEnd = b.endTimestamp;
    }
  }

  return Array.from(groups.values())
    .sort((a, b) => a.order - b.order)
    .map((g) => {
      const capacityFraction = fractionOrNull(g.maxCount, capacityLimit);
      return {
        startTimestamp: g.firstStart,
        endTimestamp: g.lastEnd,
        breakdown: {
          usagePercentage: asFraction01(g.maxUsage),
          occupancyCount: g.maxCount,
          occupancyLimit: capacityLimit > 0 ? capacityLimit : null,
          occupancyLastCount: g.latestCount,
          capacityPercentage: capacityFraction,
          capacityUtilisationLevel:
            capacityFraction === null ? null : getUtilisationLevel(capacityFraction),
          entered: g.totalIn,
          exited: g.totalOut,
          footTraffic: g.totalIn + g.totalOut,
        },
      };
    });
};

// ---------- Main entry point ----------

export const buildOccupancyModel = (input: BuildOccupancyModelInput): OccupancyModel => {
  const {
    property,
    propertyInfo,
    roomAggregations,
    roomLocationAggregations,
    monitoringHours,
    filters,
    breakdownFilters,
    tableFilters,
    isLiveView,
    barChartResolution,
    timeRange,
  } = input;

  const propertyTimezone = property?.timezone || "UTC";

  let roomsForPage = getRoomsWithAggregates(propertyInfo.rooms, roomAggregations);
  roomsForPage = applyPageLevelFilters(roomsForPage, filters);

  const roomModels = roomsForPage.map((r) =>
    prepareRoomModel(r, roomLocationAggregations, monitoringHours, propertyTimezone),
  );

  // Aggregate views (Tile, Building / Floor rows, heatmap, bar chart, summary)
  // include EVERY room with occupancy data, regardless of whether capacity is
  // configured. People physically there should be counted even if the room's
  // capacity hasn't been set; the resulting "% of capacity" is intentionally
  // inflated and the UI surfaces an orange warning on the Total Occupancy
  // tile listing the unconfigured rooms so operators can correct them.
  const aggregateRoomModels = roomModels;

  // Rooms that contributed to aggregates but have no configured capacity.
  // Exposed on `tiles.overall.unconfiguredRooms` so the tile can render a
  // warning icon with a tooltip listing them by name.
  const unconfiguredRooms = roomModels
    .filter((r) => (r.capacity ?? 0) === 0)
    .map((r) => ({ id: r.id, name: r.name }));

  const totalMonitoredHours = !isLiveView
    ? calculateTotalMonitoredHours(
        timeRange.fromTimestamp,
        timeRange.toTimestamp,
        monitoringHours,
        propertyTimezone,
      )
    : undefined;

  // ----- Utilisation table -----
  const roomRows: IOccupancyUtilisationRow[] = roomModels.map((r) =>
    buildRoomRow(r, isLiveView, totalMonitoredHours),
  );

  const filteredRoomRows = filterUtilisationRooms(roomRows, tableFilters);

  // Group by floor.
  const floors = new Map<number, IOccupancyUtilisationRow[]>();
  for (const row of filteredRoomRows) {
    const fi = row.spaceFloorIndex ?? 0;
    if (!floors.has(fi)) floors.set(fi, []);
    floors.get(fi)!.push(row);
  }

  const aggregatedRows: IOccupancyUtilisationRow[] = [];
  // Building and Floor totals aggregate only rooms with configured capacity —
  // see `aggregateRoomModels` above for why.
  if (filteredRoomRows.length) {
    aggregatedRows.push(
      buildAggregateRow(
        aggregateRoomModels.filter((rm) => filteredRoomRows.some((r) => r.spaceId === rm.id)),
        OccupancyUtilisationRowType.BUILDING,
        "Total",
        undefined,
        isLiveView,
        totalMonitoredHours,
      ),
    );
  }
  const sortedFloorIndices = Array.from(floors.keys()).sort((a, b) => a - b);
  for (const fi of sortedFloorIndices) {
    const floorRoomRows = floors.get(fi)!;
    const floorAggregateModels = aggregateRoomModels.filter(
      (rm) => rm.floorIndex === fi && floorRoomRows.some((r) => r.spaceId === rm.id),
    );
    aggregatedRows.push(
      buildAggregateRow(
        floorAggregateModels,
        OccupancyUtilisationRowType.LEVEL,
        makeFloorLevelSpaceName(fi),
        fi,
        isLiveView,
        totalMonitoredHours,
      ),
    );
    aggregatedRows.push(...floorRoomRows);
  }

  let tableData = aggregatedRows;
  if (tableFilters && tableFilters.displayBy !== "ALL") {
    tableData = tableData.filter((r) => r.rowType === tableFilters.displayBy);
  }

  const availableRoomTypes = Array.from(new Set(roomsForPage.map((r) => r.type))).sort();
  const availableFloors: IOccupancyFloor[] = Array.from(
    new Set(roomsForPage.map((r) => r.floorIndex)),
  )
    .sort((a, b) => a - b)
    .map((floorIndex) => ({ floorIndex, name: makeFloorLevelSpaceName(floorIndex) }));
  const availableSpaces: IOccupancySpace[] = roomsForPage.map((r) => ({
    id: r.id,
    name: r.name,
    type: r.type,
  }));

  const utilisation: IOccupancyUtilisationWorkerOutput = {
    tableData,
    availableRoomTypes,
    availableSpaces,
    availableFloors,
  };

  // ----- Breakdown -----
  // Heatmap / bar chart aggregate cap>0 rooms only, for the same reason as
  // the Building row: cap=0 rooms can't contribute to "% of capacity" bars.
  let breakdownRooms = aggregateRoomModels;
  if (breakdownFilters.floorIndexToDisplay >= 0) {
    breakdownRooms = breakdownRooms.filter(
      (r) => r.floorIndex === breakdownFilters.floorIndexToDisplay,
    );
  }
  if (breakdownFilters.spaceToDisplay !== "ALL") {
    if (Object.values(RoomType).includes(breakdownFilters.spaceToDisplay as RoomType)) {
      breakdownRooms = breakdownRooms.filter((r) => r.type === breakdownFilters.spaceToDisplay);
    } else {
      breakdownRooms = breakdownRooms.filter((r) => r.id === breakdownFilters.spaceToDisplay);
    }
  }
  const breakdownCapacityLimit = breakdownRooms.reduce((s, r) => s + (r.capacity ?? 0), 0);
  const breakdownData = buildBreakdownData(breakdownRooms, breakdownCapacityLimit);
  const breakdownSummary = buildSummaryForRooms(breakdownRooms, breakdownCapacityLimit, isLiveView);

  // ----- Low-res breakdown (bar chart) -----
  // Roll up the high-res breakdown client-side instead of asking the API for
  // an independently-aggregated lower resolution. Two reasons:
  //   1) API aggregates at 15-min vs 1-hour resolutions are computed from raw
  //      samples independently and can disagree by 1 at bucket boundaries —
  //      which showed up as heatmap-max != bar-chart-max for the same hour.
  //      Rolling up the heatmap data ourselves makes them match by design.
  //   2) Halves the number of aggregate API calls on page load (no separate
  //      low-res query fan-out).
  const lowResBreakdown = barChartResolution
    ? rollUpBreakdownBuckets(
        breakdownData,
        barChartResolution,
        breakdownCapacityLimit,
        propertyTimezone,
      )
    : breakdownData;

  // ----- Tiles -----
  const totalCapacity = aggregateRoomModels.reduce((s, r) => s + (r.capacity ?? 0), 0);
  const tilesSummary = buildSummaryForRooms(aggregateRoomModels, totalCapacity, isLiveView);

  const headlineOccupancy = tilesSummary.occupancyMaxCount;
  const peakDays = aggregateBy(
    aggregateRoomModels,
    totalCapacity,
    propertyTimezone,
    DATE_FORMATS.DAY_SHORT,
    isLiveView,
  );
  const peakHours = aggregateBy(
    aggregateRoomModels,
    totalCapacity,
    propertyTimezone,
    DATE_FORMATS.HOUR_24,
    isLiveView,
  );
  const glance = aggregateBy(
    aggregateRoomModels,
    totalCapacity,
    propertyTimezone,
    DATE_FORMATS.DAY_MONTH_YEAR_SLASHED,
    isLiveView,
  ).slice(0, 31);

  const tiles: OccupancyTilesData = {
    overall: {
      total: totalCapacity,
      occupied: headlineOccupancy,
      unconfiguredRooms: unconfiguredRooms.length > 0 ? unconfiguredRooms : undefined,
    },
    spaces: {
      total: aggregateRoomModels.length,
      occupied: aggregateRoomModels.filter((r) =>
        r.buckets.some((b) => b.monitored && b.occupancyMax > 0),
      ).length,
    },
    peakDays,
    peakHours,
    glance,
    ready: true,
    readingIn: tilesSummary.occupancyTotalIn,
    readingOut: tilesSummary.occupancyTotalOut,
    readingTraffic: tilesSummary.occupancyFootTraffic,
  };

  // ----- Export -----
  const exportData = buildExport(roomModels, propertyTimezone);

  // ----- Diagnostics (dev-console) -----
  const sumOfRoomPeaksMonitored = roomModels.reduce((total, r) => {
    const monitored = r.buckets.filter((b) => b.monitored && !b.isResetSample);
    if (!monitored.length) return total;
    return total + Math.max(...monitored.map((b) => b.occupancyMax));
  }, 0);
  const diagnostics = buildOccupancyDiagnostics({
    propertyInfo,
    roomAggregations,
    breakdownData,
    sumOfRoomPeaksMonitored,
  });

  return {
    tiles,
    utilisation,
    breakdown: { breakdownData, breakdownSummary },
    lowResBreakdown,
    export: exportData,
    diagnostics,
  };
};

const filterUtilisationRooms = (
  rows: IOccupancyUtilisationRow[],
  filters: IOccupancyUtilisationTableFilters | undefined,
): IOccupancyUtilisationRow[] => {
  if (!filters) return rows;
  let out = [...rows];
  if (filters.type) out = out.filter((r) => r.spaceType === filters.type);
  if (filters.usage) out = out.filter((r) => r.usage.utilisationLevel === filters.usage);
  if (filters.capacity) {
    out = out.filter((r) => {
      const hasCap = r.capacityUsage && r.capacityUsage.percentage !== null;
      return hasCap && r.capacityUsage!.utilisationLevel === filters.capacity;
    });
  }
  return out;
};

// ---------- Export ----------

const buildExport = (rooms: RoomModel[], propertyTimezone: string): OccupancyExportData => {
  const dailyData: OccupancyExportRoom[] = [];
  const hourlyData: OccupancyExportRoom[] = [];
  for (const r of rooms) {
    dailyData.push({
      name: r.name,
      rows: aggregateExportBy(r, propertyTimezone, DATE_FORMATS.DAY_MONTH_YEAR_SLASHED),
    });
    hourlyData.push({
      name: r.name,
      rows: aggregateExportBy(r, propertyTimezone, DATE_FORMATS.DAY_MONTH_YEAR_TIME_24_HOUR_SLASHED),
    });
  }
  return { dailyData, hourlyData };
};

const aggregateExportBy = (
  room: RoomModel,
  propertyTimezone: string,
  format: string,
): OccupancyExportRow[] => {
  // Per rule 6 the scoping is asymmetric:
  //   - in / out / traffic span EVERY bucket (an off-hours delivery driver
  //     walking through the door is a real event, and the utilisation
  //     table's Entered / Exited / Foot-Traffic columns already count it).
  //   - min / max / mean are business-hours questions and only consider
  //     monitored buckets, so a row's headcount columns answer "during
  //     monitored hours within this period, what were the extremes /
  //     average?" — consistent with the same fields on the table.
  //
  // Rows are emitted when the group has either monitored coverage or any
  // in/out activity. An empty unmonitored hour (no people, no entries)
  // doesn't earn a row — that would bloat the hourly export with 16+
  // zero-zero-zero lines per day.
  const groups = new Map<
    string,
    {
      date: string;
      order: number;
      min: number;
      max: number;
      meanSum: number;
      meanCount: number;
      in: number;
      out: number;
      hasMonitored: boolean;
      hasActivity: boolean;
    }
  >();
  for (const b of room.buckets) {
    if (b.isResetSample) continue;
    const parts = localiseTimestamp(b.startTimestamp, propertyTimezone);
    const date = formatLocalParts(parts, format);
    const order = Date.parse(b.startTimestamp);
    let existing = groups.get(date);
    if (!existing) {
      existing = {
        date,
        order,
        min: 0,
        max: 0,
        meanSum: 0,
        meanCount: 0,
        in: 0,
        out: 0,
        hasMonitored: false,
        hasActivity: false,
      };
      groups.set(date, existing);
    }
    existing.order = Math.min(existing.order, order);
    existing.in += b.totalIn;
    existing.out += b.totalOut;
    if (b.totalIn > 0 || b.totalOut > 0) existing.hasActivity = true;
    if (b.monitored) {
      if (!existing.hasMonitored) {
        existing.min = b.occupancyMin;
        existing.max = b.occupancyMax;
        existing.hasMonitored = true;
      } else {
        existing.min = Math.min(existing.min, b.occupancyMin);
        existing.max = Math.max(existing.max, b.occupancyMax);
      }
      existing.meanSum += b.occupancyMean;
      existing.meanCount += 1;
    }
  }
  return Array.from(groups.values())
    .filter((g) => g.hasMonitored || g.hasActivity)
    .sort((a, b) => a.order - b.order)
    .map((g) => ({
      date: g.date,
      min: g.min,
      max: g.max,
      mean: g.meanCount > 0 ? Math.ceil(g.meanSum / g.meanCount) : 0,
      in: g.in,
      out: g.out,
      traffic: g.in + g.out,
    }));
};
